import { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Mail, Printer, Sparkles } from "lucide-react";
import { submitGiggleEngagement } from "@/lib/api";

const INK = "#16324F";
const CYAN = "#40CFDE";
const CYAN_DARK = "#1D748F";
const PINK_DARK = "#D93D8F";

const SETUP_FULL = 5000;
const SETUP_PROMO = 4000;
const RA_FULL = 10000;
const RA_PROMO = 8000;
const PLATFORM_MONTHLY = 1000;
const PER_SEAT_YR = 30;

const ROLES = [
  { key: "cco",     label: "Fractional CCO",            rate: 400, discountable: true,  min: 2, max: 60, def: 10 },
  { key: "bsa",     label: "BSA/AML Officer",           rate: 200, discountable: true,  min: 2, max: 60, def: 10 },
  { key: "analyst", label: "Compliance Analyst",        rate: 150, discountable: true,  min: 2, max: 80, def: 10 },
  { key: "admin",   label: "Admin / Ops & Tech Support", rate: 75,  discountable: false, min: 2, max: 80, def: 10 },
];

const commitDiscount = (hours) => {
  if (hours >= 40) return 20;
  if (hours >= 20) return 15;
  if (hours >= 10) return 10;
  return 0;
};

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

export function EngagementBuilder({ engagementPct, sectionsViewed, onPrint }) {
  const [signedThisMonth, setSignedThisMonth] = useState(true);
  const [riskAssessment, setRiskAssessment] = useState(true);
  const [roles, setRoles] = useState(() =>
    Object.fromEntries(ROLES.map((r) => [r.key, { on: false, hours: r.def, mode: "committed" }]))
  );
  const [seats, setSeats] = useState(40);
  const [form, setForm] = useState({ open: false, name: "", email: "", note: "", sending: false, sent: false });

  const setRole = (key, patch) => setRoles((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const teamLines = ROLES.filter((r) => roles[r.key].on).map((r) => {
    const st = roles[r.key];
    const pct = r.discountable && st.mode === "committed" ? commitDiscount(st.hours) : 0;
    const effRate = r.rate * (1 - pct / 100);
    return { ...r, hours: st.hours, mode: st.mode, discount_pct: pct, effRate, monthly: Math.round(st.hours * effRate) };
  });
  const teamMonthly = teamLines.reduce((s, l) => s + l.monthly, 0);
  const raFee = riskAssessment ? (signedThisMonth ? RA_PROMO : RA_FULL) : 0;
  const bundled = riskAssessment && signedThisMonth;
  const setupFee = bundled ? 0 : (signedThisMonth ? SETUP_PROMO : SETUP_FULL);
  const oneTimeTotal = raFee + setupFee;
  const bundleSavings = bundled ? (RA_FULL - RA_PROMO) + SETUP_FULL : 0;
  const trainingAnnual = seats * PER_SEAT_YR;
  const monthlyTotal = PLATFORM_MONTHLY + teamMonthly;

  const sendToColin = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setForm((f) => ({ ...f, sending: true }));
    try {
      const res = await submitGiggleEngagement({
        name: form.name.trim(),
        email: form.email.trim(),
        note: form.note.trim() || null,
        signed_this_month: signedThisMonth,
        risk_assessment: riskAssessment,
        risk_assessment_fee: raFee,
        setup_fee: setupFee,
        one_time_total: oneTimeTotal,
        bundle_savings: bundleSavings,
        platform_monthly: PLATFORM_MONTHLY,
        team: teamLines.map((l) => ({
          key: l.key, label: l.label, hours: l.hours, mode: l.mode,
          rate: l.rate, discount_pct: l.discount_pct, monthly: l.monthly,
        })),
        team_monthly: teamMonthly,
        training_seats: seats,
        training_annual: trainingAnnual,
        monthly_total: monthlyTotal,
        engagement_pct: engagementPct,
        sections_viewed: sectionsViewed,
      });
      if (res?.ok) {
        setForm((f) => ({ ...f, sending: false, sent: true }));
        toast.success("Sent — Colin will follow up to kick things off.");
      } else {
        setForm((f) => ({ ...f, sending: false }));
        toast.error("Something went wrong — please try again.");
      }
    } catch (e) {
      setForm((f) => ({ ...f, sending: false }));
      toast.error("Network error — email colindarke@complisun.com directly.");
    }
  };

  return (
    <section id="build" className="anchor-target py-14 md:py-20" data-testid="engagement-builder">
      <span className="gf-eyebrow">Design your engagement · live pricing</span>
      <h2 className="gf-h text-4xl md:text-5xl mt-3 max-w-3xl">
        Build your compliance department. <span className="gf-grad-text">Watch the price update live.</span>
      </h2>
      <p className="mt-5 text-lg leading-relaxed max-w-3xl" style={{ color: INK, opacity: 0.78 }}>
        Start with just the platform, or bring on the whole fractional team. Compliance roles earn a
        commitment discount when you lock in monthly hours — <strong>10+ hrs → 10% off · 20+ → 15% · 40+ → 20%</strong>.
        Download it as a PDF or send it straight to Colin to start.
      </p>

      {/* What this normally costs */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl" data-testid="savings-context">
        {[
          { label: "Risk Assessment", typical: "Typical consulting / law firm", typicalPrice: "$25,000–$45,000", ours: "$10,000", oursNote: "one-time" },
          { label: "Compliance Platform (SaaS)", typical: "Typical GRC / compliance SaaS", typicalPrice: "$2,500–$5,000/mo + $10–20K setup", ours: "$1,000/mo", oursNote: "flat · no setup markup" },
        ].map((c) => (
          <div key={c.label} className="gf-card p-5" data-testid={`savings-${c.label === "Risk Assessment" ? "ra" : "platform"}`}>
            <div className="gf-eyebrow mb-2">What this normally costs</div>
            <div className="gf-h text-[16px]" style={{ color: INK }}>{c.label}</div>
            <div className="mt-2 flex items-baseline justify-between gap-3 text-[13px]">
              <span style={{ color: INK, opacity: 0.6 }}>{c.typical}</span>
              <span className="line-through font-bold whitespace-nowrap" style={{ color: INK, opacity: 0.45 }}>{c.typicalPrice}</span>
            </div>
            <div className="mt-1.5 flex items-baseline justify-between gap-3">
              <span className="text-[13px] font-bold" style={{ color: PINK_DARK }}>CompliSun</span>
              <span className="gf-h text-xl whitespace-nowrap" style={{ color: CYAN_DARK }}>{c.ours}<span className="text-[12px] font-normal opacity-60"> {c.oursNote}</span></span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-9 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5 items-start">
        <div className="space-y-4">
          {/* Risk Assessment — separate deliverable */}
          <div className="gf-card p-6" style={{ borderColor: riskAssessment ? PINK_DARK : undefined }} data-testid="ra-card">
            <div className="gf-eyebrow mb-1" style={{ color: PINK_DARK }}>Start here · Risk Assessment · Separate deliverable</div>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <label className="flex items-start gap-3 cursor-pointer flex-1 min-w-[220px]">
                <input type="checkbox" checked={riskAssessment} onChange={(e) => setRiskAssessment(e.target.checked)}
                       data-testid="ra-toggle" className="h-4 w-4 mt-1 accent-[#D93D8F]" />
                <div>
                  <div className="gf-h text-base" style={{ color: INK }}>Formal Risk Assessment</div>
                  <ul className="text-[12px] mt-1.5 space-y-1" style={{ color: INK, opacity: 0.7 }}>
                    <li>· The foundational deliverable every governance program needs first</li>
                    <li>· Onboarding form + staff working sessions</li>
                    <li>· Prioritized gap &amp; remediation report — not just a report</li>
                    <li>· No obligation to continue after delivery</li>
                  </ul>
                </div>
              </label>
              <div className="text-right">
                <div className="gf-h text-xl" style={{ color: INK }} data-testid="ra-fee">
                  {riskAssessment && signedThisMonth && <span className="line-through mr-2 text-sm opacity-40">{fmt(RA_FULL)}</span>}
                  {riskAssessment ? fmt(raFee) : fmt(RA_FULL)}
                  <span className="text-sm opacity-55"> one-time</span>
                </div>
                {riskAssessment && signedThisMonth && (
                  <div className="text-[12px] font-bold" style={{ color: PINK_DARK }}>20% off — signed this month</div>
                )}
              </div>
            </div>
            {bundled && (
              <div className="mt-4 rounded-xl px-4 py-3 text-[13px] font-bold" data-testid="ra-bundle-note"
                   style={{ background: `${PINK_DARK}12`, color: PINK_DARK }}>
                Bundle &amp; save — Risk Assessment + Full Program signed this month: 20% off the assessment and the {fmt(SETUP_FULL)} set-up fee is waived entirely → {fmt(bundleSavings)} total savings.
              </div>
            )}
          </div>

          {/* Setup + platform */}
          <div className="gf-card p-6">
            <div className="gf-eyebrow mb-4">Step 1 · The base</div>
            <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b" style={{ borderColor: "#E5EDF5" }}>
              <div>
                <div className="gf-h text-base" style={{ color: INK }}>Set-up / implementation</div>
                <div className="text-[12px]" style={{ color: INK, opacity: 0.6 }}>One-time base build-out, configured to Giggle's workflows</div>
              </div>
              <div className="text-right">
                <div className="gf-h text-xl" style={{ color: INK }} data-testid="setup-fee">
                  {bundled ? (
                    <>
                      <span className="line-through mr-2 text-sm opacity-40">{fmt(SETUP_FULL)}</span>
                      <span style={{ color: PINK_DARK }}>WAIVED</span>
                    </>
                  ) : (
                    <>
                      {signedThisMonth && <span className="line-through mr-2 text-sm opacity-40">{fmt(SETUP_FULL)}</span>}
                      {fmt(setupFee)}
                    </>
                  )}
                </div>
                {bundled && (
                  <div className="text-[11px] font-bold" style={{ color: PINK_DARK }}>bundled with Risk Assessment</div>
                )}
                <label className="flex items-center gap-1.5 text-[12px] font-bold cursor-pointer justify-end" style={{ color: PINK_DARK }}>
                  <input type="checkbox" checked={signedThisMonth} onChange={(e) => setSignedThisMonth(e.target.checked)}
                         data-testid="setup-promo-toggle" className="accent-current" />
                  Signed this month
                </label>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4">
              <div>
                <div className="gf-h text-base" style={{ color: INK }}>Platform fee — full CMS</div>
                <div className="text-[12px]" style={{ color: INK, opacity: 0.6 }}>Policies · complaints · marketing review · vendor oversight · monitoring · issue tracking</div>
              </div>
              <div className="gf-h text-xl" style={{ color: INK }}>{fmt(PLATFORM_MONTHLY)}<span className="text-sm opacity-55">/mo flat</span></div>
            </div>
          </div>

          {/* Fractional team */}
          <div className="gf-card p-6">
            <div className="gf-eyebrow mb-1">Step 2 · Your fractional team</div>
            <p className="text-[13px] mb-4" style={{ color: INK, opacity: 0.65 }}>
              Committed hours lock the discount on compliance roles. Ad-hoc keeps full flexibility at the standard rate.
            </p>
            <div className="space-y-3">
              {ROLES.map((r) => {
                const st = roles[r.key];
                const pct = r.discountable && st.mode === "committed" ? commitDiscount(st.hours) : 0;
                const effRate = r.rate * (1 - pct / 100);
                return (
                  <div key={r.key} className="rounded-2xl border p-4 transition-colors"
                       style={{ borderColor: st.on ? CYAN : "#E5EDF5", background: st.on ? `${CYAN}0d` : "#fff" }}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" checked={st.on} onChange={(e) => setRole(r.key, { on: e.target.checked })}
                               data-testid={`role-toggle-${r.key}`} className="h-4 w-4 accent-[#1D748F]" />
                        <div>
                          <div className="gf-h text-[15px]" style={{ color: INK }}>{r.label}</div>
                          <div className="text-[12px]" style={{ color: INK, opacity: 0.6 }}>
                            ${r.rate}/hr{r.discountable ? " · commitment discount eligible" : " · flat rate"}
                          </div>
                        </div>
                      </label>
                      {st.on && (
                        <div className="gf-h text-lg" data-testid={`role-monthly-${r.key}`} style={{ color: CYAN_DARK }}>
                          {st.mode === "committed" ? `${fmt(st.hours * effRate)}/mo` : `~${fmt(st.hours * r.rate)}/mo est.`}
                        </div>
                      )}
                    </div>
                    {st.on && (
                      <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-3 items-center">
                        <div>
                          <input type="range" min={r.min} max={r.max} value={st.hours}
                                 onChange={(e) => setRole(r.key, { hours: Number(e.target.value) })}
                                 data-testid={`role-hours-${r.key}`} className="w-full accent-[#D93D8F]" />
                          <div className="text-[12px] font-bold" style={{ color: INK }}>
                            {st.hours} hrs/mo
                            {pct > 0 && <span className="ml-2 px-2 py-0.5 rounded-full text-[11px]" style={{ background: `${PINK_DARK}15`, color: PINK_DARK }}>{pct}% off → ${Math.round(effRate)}/hr</span>}
                            {r.discountable && st.mode === "committed" && pct === 0 && <span className="ml-2 text-[11px] opacity-55">commit 10+ hrs to unlock 10% off</span>}
                          </div>
                        </div>
                        {r.discountable && (
                          <div className="flex rounded-full border overflow-hidden text-[12px] font-bold" style={{ borderColor: `${CYAN_DARK}30` }}>
                            {["committed", "adhoc"].map((m) => (
                              <button key={m} type="button" onClick={() => setRole(r.key, { mode: m })}
                                      data-testid={`role-mode-${r.key}-${m}`}
                                      className="px-3 py-1.5 transition-colors"
                                      style={{ background: st.mode === m ? CYAN_DARK : "#fff", color: st.mode === m ? "#fff" : CYAN_DARK }}>
                                {m === "committed" ? "Committed" : "Ad-hoc"}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Training seats */}
          <div className="gf-card p-6">
            <div className="gf-eyebrow mb-1">Step 3 · Training seats</div>
            <p className="text-[13px] mb-3" style={{ color: INK, opacity: 0.65 }}>
              The 8-module, 5-track program — ${PER_SEAT_YR}/seat/year, no setup fee. Billed annually, separate from the platform.
            </p>
            <input type="range" min={10} max={300} step={5} value={seats}
                   onChange={(e) => setSeats(Number(e.target.value))}
                   data-testid="builder-seats-slider" className="w-full accent-[#1D748F]" />
            <div className="flex justify-between items-baseline mt-1">
              <div className="text-[13px] font-bold" style={{ color: INK }}>{seats} seats</div>
              <div className="gf-h text-lg" style={{ color: CYAN_DARK }} data-testid="builder-training-annual">{fmt(trainingAnnual)}/yr</div>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="gf-card p-6 lg:sticky lg:top-24" style={{ background: INK, color: "#fff", borderColor: INK }} data-testid="engagement-summary">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.16em]" style={{ color: CYAN }}>Your engagement</div>
          <div className="mt-4 space-y-2.5 text-[14px]">
            {riskAssessment && (
              <div className="flex justify-between"><span style={{ opacity: 0.75 }}>Risk Assessment (one-time)</span><strong data-testid="summary-ra">{fmt(raFee)}</strong></div>
            )}
            <div className="flex justify-between"><span style={{ opacity: 0.75 }}>Set-up (one-time)</span><strong data-testid="summary-setup">{bundled ? "Waived" : fmt(setupFee)}</strong></div>
            <div className="flex justify-between"><span style={{ opacity: 0.75 }}>Platform (flat)</span><strong>{fmt(PLATFORM_MONTHLY)}/mo</strong></div>
            {teamLines.map((l) => (
              <div key={l.key} className="flex justify-between text-[13px]">
                <span style={{ opacity: 0.7 }}>{l.label} · {l.hours}h {l.discount_pct ? `(-${l.discount_pct}%)` : ""}{l.mode === "adhoc" ? " · ad-hoc" : ""}</span>
                <span>{fmt(l.monthly)}/mo</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 border-t" style={{ borderColor: "rgba(255,255,255,0.15)" }}>
              <span style={{ opacity: 0.75 }}>Training ({seats} seats)</span><strong>{fmt(trainingAnnual)}/yr</strong>
            </div>
          </div>
          <div className="mt-5 pt-4 border-t" style={{ borderColor: "rgba(255,255,255,0.2)" }}>
            <div className="text-[11px] uppercase tracking-[0.14em]" style={{ opacity: 0.6 }}>Monthly total (platform + team)</div>
            <div className="gf-h text-4xl mt-1" style={{ color: CYAN }} data-testid="summary-monthly-total">{fmt(monthlyTotal)}<span className="text-lg">/mo</span></div>
            <div className="text-[12px] mt-1" style={{ opacity: 0.65 }} data-testid="summary-onetime">+ {fmt(oneTimeTotal)} one-time · + {fmt(trainingAnnual)}/yr training</div>
            {bundled && (
              <div className="mt-3 rounded-xl px-3 py-2 text-center" style={{ background: "rgba(217,61,143,0.2)", border: `1px solid ${PINK_DARK}` }} data-testid="summary-savings">
                <span className="gf-h text-lg" style={{ color: "#FF9ECF" }}>{fmt(bundleSavings)}</span>
                <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] ml-2" style={{ color: "#FF9ECF" }}>total savings</span>
              </div>
            )}
          </div>

          <div className="mt-6 space-y-2" data-print="hide">
            <button type="button" onClick={() => setForm((f) => ({ ...f, open: !f.open }))}
                    data-testid="engagement-email-toggle"
                    className="gf-cta-pink w-full inline-flex items-center justify-center gap-2 h-12 text-[14px]">
              <Mail className="h-4 w-4" /> Email this to Colin to start
            </button>
            <button type="button" onClick={onPrint} data-testid="engagement-pdf"
                    className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full text-[14px] font-bold border-2"
                    style={{ borderColor: "rgba(255,255,255,0.35)", color: "#fff" }}>
              <Printer className="h-4 w-4" /> Download as PDF
            </button>
          </div>

          {form.open && !form.sent && (
            <div className="mt-4 space-y-2.5" data-print="hide" data-testid="engagement-email-form">
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                     placeholder="Your name" data-testid="engagement-name"
                     className="w-full rounded-xl px-3.5 py-2.5 text-[14px]"
                     style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff" }} />
              <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                     placeholder="you@gigglefinance.com" type="email" data-testid="engagement-email"
                     className="w-full rounded-xl px-3.5 py-2.5 text-[14px]"
                     style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff" }} />
              <textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                        placeholder="Anything Colin should know? (optional)" rows={2} data-testid="engagement-note"
                        className="w-full rounded-xl px-3.5 py-2.5 text-[14px] resize-none"
                        style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff" }} />
              <button type="button" onClick={sendToColin} disabled={form.sending}
                      data-testid="engagement-send"
                      className="gf-cta w-full inline-flex items-center justify-center gap-2 h-12 text-[14px] disabled:opacity-50">
                {form.sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {form.sending ? "Sending…" : "Send my engagement"}
              </button>
            </div>
          )}
          {form.sent && (
            <div className="mt-4 rounded-xl p-4 text-center" style={{ background: "rgba(64,207,222,0.15)", border: `1px solid ${CYAN}` }} data-testid="engagement-sent">
              <Check className="h-6 w-6 mx-auto mb-1" style={{ color: CYAN }} />
              <div className="gf-h text-base">Sent to Colin.</div>
              <div className="text-[12px] mt-1" style={{ opacity: 0.7 }}>He'll reply within one business day to schedule the free discovery call.</div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
